import React, { useState, useMemo, useEffect, useCallback } from "react";
import ContentLayout from "../../../layouts/ContentLayout";
import { useOutletContext } from "react-router-dom";
import * as Yup from "yup";
import { SIDE_MENU } from "../constants";
import SelectTestSchedule from "../../../components/UI/Form/Select/SelectTestSchedule";
import Item from "./Item";
import Button from "../../../components/UI/Button";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import validateMessages from "../../../validateMessages";
import { commonServices, invoiceServices } from "../../../services/api";
import messages from "../../../services/api/messages";
import { fileNameType } from "../../../constants";
import moment from "moment";

const BatchInvoicePage = () => {
  const [breadcrumb] = useOutletContext();
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [initInvoices, setInitInvoices] = useState([]);
  const [hasAvailableData, setHasAvailableData] = useState(false);
  const [haveDownloadFiles, setHaveDownloadFiles] = useState(false);
  const [hasDownloadFileBillExcel, setHasDownloadedFileBillExcel] =
    useState(false);
  const [hasDownloadFileBillPdf, setHasDownloadedFileBillPdf] = useState(false);
  const [hasDownloadFileDeliveryExcel, setHasDownloadedFileDeliveryExcel] =
    useState(false);
  const [hasDownloadFileDeliveryPdf, setHasDownloadedFileDeliveryPdf] =
    useState(false);

  const itemDataArray = useMemo(() => {
    return (
      selectedSchedule?.caseInfos.filter(
        (caseInfo) =>
          !caseInfo.invoice || caseInfo.invoiceStatus === "notStarted"
      ) || []
    );
  }, [selectedSchedule]);

  const loadInitInvoices = useCallback(async () => {
    if (!itemDataArray?.length === 0) {
      return;
    }

    let invoices = [];
    for (const itemData of itemDataArray) {
      try {
        const data = await invoiceServices.getInitNewInvoice(
          itemData.customer._id,
          itemData.quotation
        );

        invoices.push(data);
      } catch (error) {
        toast.error(messages.callApiFail);
      }
    }

    setInitInvoices(invoices);
  }, [itemDataArray]);

  useEffect(() => {
    loadInitInvoices();
  }, [loadInitInvoices]);

  const cellStyle =
    "flex items-center justify-center p-2 bg-gray-200 border border-[#B3B3B3]";

  const formik = useFormik({
    initialValues: itemDataArray?.map((item) => {
      return {
        memo: "",
        paymentDeadline: "",
        isConfirmed: false,
      };
    }),
    validationSchema: Yup.array().of(
      Yup.object({
        memo: Yup.string(),
        paymentDeadline: Yup.string(),
        isConfirmed: Yup.boolean(),
      })
    ),
    enableReinitialize: true,
  });

  const { validateForm } = formik;

  const handleConfirm = async () => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      const combinedDataArray = itemDataArray
        .map((item, index) => {
          return {
            ...item,
            ...initInvoices[index],
            ...formik.values[index],
          };
        })
        .filter((item) => {
          return item.isConfirmed;
        });

      try {
        const results = await Promise.all(
          combinedDataArray.map(async (item, index) => {
            const createFileData = {
              caseInfoId: item._id,
              customerId: item.customer._id,
              testScheduleId: selectedSchedule._id,
              personInCharge: item.customerPic,
              memo: item.memo,
              paymentDeadline: item.paymentDeadline,
              invoiceItemInfos: Array.isArray(item.invoiceItemInfos)
                ? item.invoiceItemInfos.map((childItem) => ({
                    name: childItem.name,
                    contractType: childItem.contractType,
                    volumn: childItem.volumn,
                    testPeopleNumber: childItem.testPeopleNumber,
                    testSampleNumber: childItem.testSampleNumber,
                    unitPrice: childItem.unitPrice,
                    totalPrice:
                      Number(childItem.unitPrice) *
                      Number(childItem.testSampleNumber),
                  }))
                : [],
            };

            const res = await invoiceServices.createFileDownload(createFileData);
            const caseId = item.caseId || "";
            const invoiceFileType = fileNameType["invoice"];
            const deliveryFileType = fileNameType["delivery"];
            const timeStamp = moment().format("YYYYMMDDHHmmss");
            const customerName = item.customer.companyNameKanji;

            await new Promise((resolve) => setTimeout(resolve, 1000));

            try {
              await commonServices.downloadWithFileId(
                res.data.fileBill._id,
                "xlsx",
                `${invoiceFileType}_${caseId}_${customerName}_${timeStamp}`
              );
              setHasDownloadedFileBillExcel(true);
            } catch {
              toast.error(messages.downloadFail);
            }
            try {
              await commonServices.downloadPDFWithFileId(
                res.data.fileBill._id,
                `${invoiceFileType}_${caseId}_${customerName}_${timeStamp}`
              );
              setHasDownloadedFileBillPdf(true);
            } catch {
              toast.error(messages.downloadFail);
            }
            try {
              await commonServices.downloadWithFileId(
                res.data.fileDeliverly._id,
                "xlsx",
                `${deliveryFileType}_${caseId}_${customerName}_${timeStamp}`
              );
              setHasDownloadedFileDeliveryExcel(true);
            } catch {
              toast.error(messages.downloadFail);
            }
            try {
              await commonServices.downloadPDFWithFileId(
                res.data.fileDeliverly._id,
                `${deliveryFileType}_${caseId}_${customerName}_${timeStamp}`
              );
              setHasDownloadedFileDeliveryPdf(true);
            } catch {
              toast.error(messages.downloadFail);
            }

            return {
              index,
              generatedInvoiceExcel: res.data.fileBill._id,
              generatedDeliverySlipExcel: res.data.fileDeliverly._id,
              quotationId: caseId,
            };
          })
        );

        setInitInvoices((prevInvoices) => {
          const newInvoices = [...prevInvoices];
          results.forEach((result) => {
            if (result.index !== undefined && newInvoices[result.index]) {
              newInvoices[result.index] = {
                ...newInvoices[result.index],
                generatedInvoiceExcel: result.generatedInvoiceExcel,
                generatedDeliverySlipExcel: result.generatedDeliverySlipExcel,
              };
            }
          });
          return newInvoices;
        });

        toast.success("請求書一括発行が完了しました。");
      } catch (error) {
        toast.error(messages.callApiFail);
      }
    } else {
      toast.error(validateMessages.required("振込期限"));
    }
  };

  const handleCreate = async () => {
    const errors = await validateForm();

    if (Object.keys(errors).length === 0) {
      const combinedDataArray = itemDataArray
        .map((item, index) => {
          return {
            ...item,
            ...initInvoices[index],
            ...formik.values[index],
          };
        })
        .filter((item) => {
          return item.isConfirmed;
        });

      try {
        const promises = combinedDataArray.map((item) => {
          const createData = {
            caseInfoId: item._id,
            customerId: item.customer._id,
            testScheduleId: selectedSchedule._id,
            personInCharge: item.customerPic,
            memo: item.memo,
            paymentDeadline: item.paymentDeadline,
            invoiceItemInfos: Array.isArray(item.invoiceItemInfos)
              ? item.invoiceItemInfos.map((childItem) => ({
                  name: childItem.name,
                  contractType: childItem.contractType,
                  volumn: childItem.volumn,
                  testPeopleNumber: childItem.testPeopleNumber,
                  testSampleNumber: childItem.testSampleNumber,
                  unitPrice: childItem.unitPrice,
                  totalPrice:
                    Number(childItem.unitPrice) *
                    Number(childItem.testSampleNumber),
                }))
              : [],
            generatedInvoiceExcel: item.generatedInvoiceExcel,
            generatedDeliverySlipExcel: item.generatedDeliverySlipExcel,
          };
          return invoiceServices.createInvoice(createData);
        });

        const results = await Promise.all(promises);

        toast.success(messages.create(true));
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        toast.error(messages.create(false));
      }
    } else {
      toast.error(validateMessages.required("振込期限"));
    }
  };

  const handleChangeCreateInvoiceInfo = (index, value) => {
    setInitInvoices((prevInvoices) => {
      const newInvoices = [...prevInvoices];
      newInvoices[index] = {
        ...newInvoices[index],
        invoiceItemInfos: value,
      };

      return newInvoices;
    });
  };

  useEffect(() => {
    setHasAvailableData(formik.values.some((item) => item.isConfirmed));
  }, [formik.values]);

  useEffect(() => {
    if (
      hasDownloadFileBillExcel &&
      hasDownloadFileDeliveryExcel &&
      hasDownloadFileBillPdf &&
      hasDownloadFileDeliveryPdf
    ) {
      setHaveDownloadFiles(true);
    }
  }, [
    hasDownloadFileBillExcel,
    hasDownloadFileDeliveryExcel,
    hasDownloadFileBillPdf,
    hasDownloadFileDeliveryPdf,
  ]);

  return (
    <ContentLayout sideMenuLinkOptions={SIDE_MENU} breadcrumb={breadcrumb}>
      <div className="w-full h-auto">
        <div className="w-full h-auto mb-5">
          <p className="text-custom-size mb-3">請求書一括発行・送付</p>
        </div>

        <SelectTestSchedule
          isWhite
          schedule={selectedSchedule}
          setSchedule={setSelectedSchedule}
          filterFunc={(schedules) =>
            schedules
              .filter((schedule) => schedule.caseInfos.length > 0)
              .sort((a, b) => {
                const date1 = new Date(a.testDate);
                const date2 = new Date(b.testDate);
                return date2 - date1;
              })
          }
        />

        {itemDataArray?.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <div
                className="grid grid-cols-11 p-4"
                style={{
                  gridTemplateColumns:
                    "120px 70px 140px 200px 200px 120px 70px",
                }}
              >
                <div className={"border-l " + cellStyle}>案件ID</div>
                <div className={cellStyle}>請求書</div>
                <div className={cellStyle}>企業名</div>
                <div className={cellStyle}>振込期限</div>
                <div className={cellStyle}>備考</div>
                <div className={cellStyle}>確認</div>
                <div className={cellStyle}>状況</div>
                {itemDataArray?.map((item, index) => (
                  <Item
                    key={item._id}
                    index={index}
                    data={item}
                    formik={formik}
                    initInvoices={initInvoices}
                    handleChangeCreateInvoiceInfo={
                      handleChangeCreateInvoiceInfo
                    }
                  />
                ))}
              </div>
            </div>

            <div className="flex items-end justify-end mt-4 gap-6">
              <Button
                disabled={!hasAvailableData}
                type={hasAvailableData ? "primary" : "disabled"}
                onClick={handleConfirm}
              >
                請求書一括発行
              </Button>
              <Button
                disabled={!hasAvailableData || !haveDownloadFiles}
                type={
                  hasAvailableData && haveDownloadFiles ? "primary" : "disabled"
                }
                onClick={handleCreate}
              >
                請求書一括送付
              </Button>
            </div>
          </>
        )}
      </div>
    </ContentLayout>
  );
};

export default BatchInvoicePage;
